/**
 * The article's words, without the markup the editor stores alongside them.
 *
 * A stored body is Markdown plus the few literal HTML shapes Markdown cannot
 * carry: the coloured boxes, the marker pen, the checklist. That is the right
 * storage - see client/src/rich-text.ts - and it is the wrong thing to MEASURE
 * or to READ, and both of those happen in several places:
 *
 *   the write screen counts a section against its character target, and a
 *   `<div class="callout callout--warn"><p>…</p></div>` adds sixty characters
 *   nobody wrote and no reader will see - two boxes push an on-target section
 *   past the 「長い」 warning;
 *
 *   the legal review takes claim sentences out of the body to retrieve
 *   provisions for, and a claim arriving as `<div class="callout callout--warn">
 *   <p>永久に…` is markup noise in the embedding of the sentence being checked;
 *
 *   and a rule finding quotes twenty characters either side of the phrase it
 *   matched, which is how `class="callout callout--stop"><p>` ends up in the
 *   excerpt a clinic is shown and asked to go and fix.
 *
 * None of that mattered while only a human could insert a box, because they
 * rarely did. The drafting prompt now offers these shapes to the model, so
 * every generated section may carry them and every count and quote above has to
 * be about the words.
 *
 * Shared rather than written twice: the client counts characters and the server
 * checks them, and a body that measures 700 in one place and 760 in the other is
 * two different articles as far as anybody debugging is concerned.
 */

/**
 * Drop tags, keep what they contained.
 *
 * Deliberately a tag stripper and not a parser. This is never used to RENDER -
 * rendering goes through markdown-it in the editor and through the rebuilt,
 * escaped output in server/lib/markdown.ts, and neither of them trusts this.
 * It exists to answer "how much did somebody write" and "what does this
 * sentence say", and for both of those, dropping anything between angle
 * brackets is exactly right.
 */
export function visibleText(body: string): string {
  return (
    body
      // A block element is a boundary between sentences: without this, a box
      // and the paragraph after it become one run-on line to anything that
      // splits on newlines.
      .replace(/<\/(p|div|li|ul|ol|h[1-6]|blockquote|tr)>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/\n{3,}/g, '\n\n')
  );
}

/**
 * How long an article reads, in the characters a reader will actually see.
 *
 * Markdown's own syntax is left in on purpose: `##` and `|` are a handful of
 * characters, they are what every previous count included, and a target that
 * silently changed meaning would make every section written before today look
 * short. What is removed is only the HTML, which is the part that arrived in
 * volume the moment the model was allowed to emit it.
 */
export const visibleLength = (body: string): number => visibleText(body).length;
